import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { readingProgressService, type ReadingProgressDto } from '../api/readingProgressService';
import type { ReadingProgress } from '../components/reader/types';

/**
 * Convert backend ReadingProgressDto to frontend ReadingProgress type
 */
function mapDtoToProgress(dto: ReadingProgressDto): ReadingProgress {
  return {
    bookId: dto.bookId,
    userId: dto.userId,
    currentLocation: dto.currentLocation,
    currentLocationLabel: dto.currentLocationLabel,
    progressPercentage: dto.progressPercentage,
    totalReadingTime: dto.totalReadingTimeInSeconds,
    lastReadAt: new Date(dto.lastReadAt),
  };
}

/**
 * Hook to manage and auto-save reading progress for a specific book
 *
 * Features:
 * - Auto-saves progress at configurable intervals (default: 30 seconds)
 * - Only saves if progress has changed (avoids unnecessary DB calls)
 * - Tracks reading time automatically
 * - Restores last reading position
 */
export function useReadingProgress(bookId: string, saveIntervalSeconds: number = 30) {
  const queryClient = useQueryClient();
  const queryKey = ['readingProgress', bookId];

  // Refs for tracking
  const sessionStartRef = useRef<Date>(new Date());
  const lastSaveRef = useRef<Date>(new Date());
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Track last saved values to avoid unnecessary saves
  const lastSavedProgressRef = useRef<{
    currentLocation: string;
    progressPercentage: number;
  } | null>(null);

  // Fetch reading progress
  const { data: progress, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const dto = await readingProgressService.getMyProgress(bookId);
      const mapped = mapDtoToProgress(dto);

      // Initialize last saved progress when we fetch from server
      lastSavedProgressRef.current = {
        currentLocation: mapped.currentLocation,
        progressPercentage: mapped.progressPercentage,
      };

      return mapped;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - data rarely changes from server
    gcTime: 1000 * 60 * 15, // 15 minutes garbage collection
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    refetchOnReconnect: false, // Don't refetch on reconnect
    refetchOnMount: false, // Only fetch once on mount
  });

  // Update reading progress mutation
  const updateProgress = useMutation({
    mutationFn: async ({
      currentLocation,
      currentLocationLabel,
      progressPercentage,
    }: {
      currentLocation: string | number;
      currentLocationLabel: string;
      progressPercentage: number;
    }) => {
      // Calculate session reading time
      const now = new Date();
      const sessionDuration = Math.floor(
        (now.getTime() - sessionStartRef.current.getTime()) / 1000
      );

      const dto = await readingProgressService.updateMyProgress(bookId, {
        currentLocation: String(currentLocation),
        currentLocationLabel,
        progressPercentage,
        totalReadingTimeInSeconds: (progress?.totalReadingTime || 0) + sessionDuration,
      });

      // Reset session start time for next calculation
      sessionStartRef.current = now;
      lastSaveRef.current = now;

      return mapDtoToProgress(dto);
    },
    onSuccess: (data) => {
      // Update cache directly instead of invalidating (prevents refetch)
      queryClient.setQueryData(queryKey, data);
    },
  });

  // Get total reading time
  const getTotalReadingTime = async (): Promise<number> => {
    return await readingProgressService.getTotalReadingTime(bookId);
  };

  /**
   * Auto-save function that saves current progress
   * Only saves if progress has actually changed to avoid unnecessary DB calls
   */
  const autoSave = useCallback(
    async (
      currentLocation: string | number,
      currentLocationLabel: string,
      progressPercentage: number
    ) => {
      // Skip if a save is already in progress
      if (updateProgress.isPending) {
        console.debug('Save already in progress, skipping auto-save');
        return;
      }

      try {
        const locationStr = String(currentLocation);

        // Guard: empty/whitespace location fails backend [Required] validation
        // with a 400. This happens on the first auto-save interval firing
        // before epub.js has emitted its first `relocated` event (so the
        // renderer's currentCFI state is still ''). Skip silently — the next
        // interval will have a real CFI.
        if (!locationStr || locationStr.trim().length === 0) {
          return;
        }

        // Check if progress has changed since last save
        const hasChanged =
          !lastSavedProgressRef.current ||
          lastSavedProgressRef.current.currentLocation !== locationStr ||
          lastSavedProgressRef.current.progressPercentage !== progressPercentage;

        if (!hasChanged) {
          // Progress hasn't changed, skip save
          console.debug('Progress unchanged, skipping auto-save');
          return;
        }

        // Progress has changed, save it
        await updateProgress.mutateAsync({
          currentLocation,
          currentLocationLabel,
          progressPercentage,
        });

        // Update last saved values
        lastSavedProgressRef.current = {
          currentLocation: locationStr,
          progressPercentage,
        };

        console.debug('Auto-save successful:', { currentLocation, progressPercentage });
      } catch (error: any) {
        // Handle concurrency exceptions gracefully
        if (error?.message?.includes('concurrency') || error?.message?.includes('DbUpdateConcurrencyException')) {
          console.warn('Concurrency conflict during auto-save (another update in progress), will retry next interval');
        } else {
          console.error('Auto-save failed:', error);
        }
      }
    },
    [updateProgress]
  );

  /**
   * Start auto-save interval (configurable, default 30 seconds)
   */
  const startAutoSave = useCallback(
    (
      getCurrentLocation: () => string | number,
      getCurrentLocationLabel: () => string,
      getProgressPercentage: () => number
    ) => {
      // Clear any existing interval
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }

      // Set up auto-save at the configured interval
      const intervalMs = saveIntervalSeconds * 1000;
      autoSaveIntervalRef.current = setInterval(() => {
        const location = getCurrentLocation();
        const locationLabel = getCurrentLocationLabel();
        const percentage = getProgressPercentage();

        autoSave(location, locationLabel, percentage);
      }, intervalMs);

      console.debug(`Auto-save started with ${saveIntervalSeconds}s interval`);

      return () => {
        if (autoSaveIntervalRef.current) {
          clearInterval(autoSaveIntervalRef.current);
          autoSaveIntervalRef.current = null;
        }
      };
    },
    [autoSave, saveIntervalSeconds]
  );

  /**
   * Stop auto-save interval
   */
  const stopAutoSave = useCallback(() => {
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
      autoSaveIntervalRef.current = null;
    }
  }, []);

  /**
   * Manual save (can be called before navigating away)
   */
  const saveProgress = useCallback(
    async (
      currentLocation: string | number,
      currentLocationLabel: string,
      progressPercentage: number
    ) => {
      // Guard: empty location fails backend [Required] validation with 400.
      // Matches the guard in autoSave.
      const locationStr = String(currentLocation);
      if (!locationStr || locationStr.trim().length === 0) {
        return;
      }

      await updateProgress.mutateAsync({
        currentLocation,
        currentLocationLabel,
        progressPercentage,
      });

      // Update last saved values after successful save
      lastSavedProgressRef.current = {
        currentLocation: locationStr,
        progressPercentage,
      };
    },
    [updateProgress]
  );

  /**
   * Save progress on page change (debounced to prevent excessive calls)
   * This is called when user navigates to a new page via any method
   */
  const saveProgressOnPageChange = useCallback(
    async (
      currentLocation: string | number,
      currentLocationLabel: string,
      progressPercentage: number
    ) => {
      try {
        const locationStr = String(currentLocation);

        // Guard: empty location fails backend [Required] validation with 400.
        if (!locationStr || locationStr.trim().length === 0) {
          return;
        }

        // Check if this is actually a new page
        const hasChanged =
          !lastSavedProgressRef.current ||
          lastSavedProgressRef.current.currentLocation !== locationStr;

        if (!hasChanged) {
          // Same page, no need to save
          return;
        }

        // Page has changed, save immediately
        await updateProgress.mutateAsync({
          currentLocation,
          currentLocationLabel,
          progressPercentage,
        });

        // Update last saved values
        lastSavedProgressRef.current = {
          currentLocation: locationStr,
          progressPercentage,
        };

        console.debug('Progress saved on page change:', { currentLocation, progressPercentage });
      } catch (error: any) {
        console.error('Failed to save progress on page change:', error);
      }
    },
    [updateProgress]
  );

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      stopAutoSave();
    };
  }, [stopAutoSave]);

  return {
    progress,
    isLoading,
    error,
    saveProgress,
    saveProgressOnPageChange, // New: Save immediately when page changes
    startAutoSave,
    stopAutoSave,
    getTotalReadingTime,
    isSaving: updateProgress.isPending,
  };
}
